import React, { useState, useEffect } from 'react';
import { showToast } from 'stores/ToastStore';
import { titleize } from 'underscore.string';
import { pick } from 'lodash';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import SessionStore from '../../../../stores/SessionStore';
import {
  getShipper,
  updateShipperFscConfig,
} from '../../../../utils/CustomersAPIUtils';
import CargomaticCard from '../../../v2/card';
import EditButton from '../../../v2/buttons/EditButton';
import Alert from '../../../ui/Alert';
import CargomaticSelect from '../../../v2/select';
import CargomaticCheckbox from '../../../v2/forms/Checkbox';
import DescriptionListItem from '../../../v2/DescriptionListItem';
import seriesIds from '../../../../constants/RateSeriesIds';
import LoadingCircle from '../../../ui/LoadingCircle';

import {
  fuelWeeks,
  fuelDays,
  fuelMatrixOptions,
} from '../../../../constants/fsc';

const defaultFsc = {
  series_id: '',
  fuelWeek: '',
  fuelDay: '',
  fuelMatrix: '',
};

function FscSettings({ shipperId, t }) {
  const [editing, setEditing] = useState(false);
  const [fscConfiguration, setFscConfiguration] = useState(null);
  const [applied, setApplied] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [loaded, setLoaded] = useState(false);

  function handleSessionStoreChange() {
    setCanEdit(SessionStore.isAbleToEditShorthaulFSC());
  }

  useEffect(() => {
    async function load() {
      const res = await getShipper(shipperId);

      const data = res.entity;
      setFscConfiguration(data.fsc_config || defaultFsc);
      setApplied(data.use_fsc);
      setLoaded(true);
    }
    load();

    setCanEdit(SessionStore.isAbleToEditShorthaulFSC());
    SessionStore.addChangeListener(handleSessionStoreChange);

    return () => {
      SessionStore.removeChangeListener(handleSessionStoreChange);
    };
  }, []);

  function handleEdit() {
    setEditing(true);
  }

  function handleCancel() {
    setEditing(false);
  }

  async function handleSave(updates) {
    let finalUpdates;

    if (updates.hasOwnProperty('use_fsc') && !updates.use_fsc) {
      finalUpdates = { use_fsc: false };
    } else {
      finalUpdates = updates;
    }
    try {
      const {
        entity: { data },
      } = await updateShipperFscConfig(shipperId, finalUpdates);
      showToast(t('UPDATE_FSC_CONFIG'), t('SUCCESSFULLY_UPDATED_FSC'));
      setFscConfiguration(data.fsc_config);
      setApplied(data.use_fsc);
    } catch (e) {
      console.log(e);
      showToast(t('UPDATE_FSC_CONFIG'), t('FAILURE_FSC_UPDATE'), 'error');
    } finally {
      setEditing(false);
    }
  }

  function renderHeader(headerParams) {
    const { canEdit } = headerParams;
    return <Header canEdit={canEdit} onEdit={handleEdit} t={t} />;
  }

  return (
    <CargomaticCard
      header={renderHeader}
      headerParams={{ canEdit: SessionStore.isAbleToEditShorthaulFSC() }}
    >
      {!loaded && <LoadingCircle />}

      {loaded && !editing && (
        <FscSettingsSummary
          fscApplied={applied}
          fscConfiguration={fscConfiguration}
          t={t}
        />
      )}
      {canEdit && loaded && editing && (
        <FscSettingsForm
          fscConfiguration={fscConfiguration}
          fscApplied={applied}
          onSave={handleSave}
          onCancel={handleCancel}
          t={t}
        />
      )}
    </CargomaticCard>
  );
}

function FscSettingsForm({
  fscApplied,
  fscConfiguration,
  onSave,
  onCancel,
  t,
}) {
  const [errors, setErrors] = useState({});
  const [applied, setApplied] = useState(false);
  const [updates, setUpdates] = useState(null);

  const series_id = updates?.series_id || '';
  const fuelWeek = updates?.fuelWeek || '';
  const fuelDay = updates?.fuelDay || '';
  const fuelMatrix = updates?.fuelMatrix || '';

  useEffect(() => {
    setApplied(fscApplied);
  }, [fscApplied]);

  useEffect(() => {
    const mappedUpdates = pick(fscConfiguration || {}, [
      'series_id',
      'fuelWeek',
      'fuelDay',
      'fuelMatrix',
    ]);
    setUpdates(mappedUpdates);
  }, [fscConfiguration]);

  function handleSave() {
    const formErrors = {};

    if (!applied) {
      setErrors(formErrors);
      onSave({ use_fsc: applied, fsc_config: null });
      return;
    }

    if (series_id || fuelWeek || fuelDay || fuelMatrix) {
      if (!series_id) {
        formErrors.series_id = t('SELECT_SERIES_ID_TIP');
      }
      if (!fuelWeek) {
        formErrors.fuelWeek = t('SELECT_FUEL_WEEK_TIP');
      }
      if (!fuelDay) {
        formErrors.fuelDay = t('SELECT_FUEL_DAY_TIP');
      }
      if (!fuelMatrix) {
        formErrors.fuelMatrix = t('SELECT_FUEL_MATRIX_TIP');
      }
    }

    setErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      const isConfigEmpty = Object.values(updates || {}).every(
        (value) => !value,
      );
      onSave({ use_fsc: applied, fsc_config: isConfigEmpty ? null : updates });
    }
  }

  function handleFieldChange(field) {
    return function ({ value }) {
      setUpdates({
        ...updates,
        [field]: value || '',
      });
    };
  }

  function handleToggleApplied(checked) {
    setApplied(checked);
  }

  function renderErrors() {
    if (Object.keys(errors).length === 0) {
      return null;
    }

    return (
      <div className="mb-4">
        <Alert type="error" title={titleize(t('ERROR', { count: 2 }))}>
          <ul>
            {Object.values(errors).map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </Alert>
      </div>
    );
  }

  return (
    <div className="">
      {renderErrors()}
      <div className="mb-4">
        <CargomaticCheckbox
          id="sharedFilter"
          checked={applied}
          onChange={handleToggleApplied}
        >
          {t('APPLIED')}
        </CargomaticCheckbox>
      </div>
      {applied && (
        <div>
          <CargomaticSelect
            className="mb-4"
            valueKey="value"
            textKey="name"
            label={t('SERIES_ID')}
            items={seriesIds}
            selected={series_id}
            showCaption
            caption={t('SELECT_SERIES_ID')}
            onSelect={handleFieldChange('series_id')}
          />
          <CargomaticSelect
            className="mb-4"
            valueKey="value"
            textKey="localizationSlug"
            label={t('FUEL_WEEK')}
            items={fuelWeeks}
            selected={fuelWeek}
            showCaption
            caption={t('SELECT_FUEL_WEEK')}
            onSelect={handleFieldChange('fuelWeek')}
          />
          <CargomaticSelect
            className="mb-4"
            valueKey="value"
            textKey="localizationSlug"
            label={t('FUEL_DAY')}
            items={fuelDays}
            selected={fuelDay}
            showCaption
            caption={t('SELECT_DAY')}
            onSelect={handleFieldChange('fuelDay')}
          />
          <CargomaticSelect
            className="mb-4"
            valueKey="value"
            textKey="localizationSlug"
            label={t('FUEL_MATRIX')}
            items={fuelMatrixOptions}
            selected={fuelMatrix}
            showCaption
            caption={t('SELECT_FUEL_MATRIX')}
            onSelect={handleFieldChange('fuelMatrix')}
          />
        </div>
      )}
      <div className="flex justify-end items-center space-x-2">
        <button
          type="button"
          className="button button-green"
          onClick={handleSave}
        >
          {titleize(t('SAVE'))}
        </button>
        <button
          type="button"
          className="button button-secondary"
          onClick={onCancel}
        >
          {t('CANCEL')}
        </button>
      </div>
    </div>
  );
}

function FscSettingsSummary({ fscApplied, fscConfiguration, t }) {
  return (
    <div>
      {!fscApplied && <Alert type="warning">{t('FSC_NOT_APPLIED')}</Alert>}
      {fscApplied && (
        <div className="sm:grid sm:grid-cols-2 sm:gap-3">
          <DescriptionListItem label={t('SERIES_ID')} className="mb-4">
            {seriesIds.find(
              (config) => config.value === fscConfiguration?.series_id,
            )?.name || t('NOT_SET')}
          </DescriptionListItem>
          <DescriptionListItem label={t('FUEL_WEEK')} className="mb-4">
            {titleize(t(sluggify(fscConfiguration?.fuelWeek))) || t('NOT_SET')}
          </DescriptionListItem>
          <DescriptionListItem label={t('FUEL_DAY')} className="mb-4">
            {titleize(t(sluggify(fscConfiguration?.fuelDay))) || t('NOT_SET')}
          </DescriptionListItem>
          <DescriptionListItem label={t('FUEL_MATRIX')} className="mb-4">
            {titleize(t(sluggify(fscConfiguration?.fuelMatrix))) ||
              t('NOT_SET')}
          </DescriptionListItem>
        </div>
      )}
    </div>
  );
}

function Header({ canEdit, onEdit, t }) {
  return (
    <div className="flex justify-between items-center">
      <h3 className="my-0 flex-1 mr-4">{t('SHORTHAUL_FUEL_SURCHARGE')}</h3>
      <EditButton size="small" onClick={onEdit} disabled={!canEdit} />
    </div>
  );
}

export default withTranslation()(FscSettings);
